import React, { useMemo } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
} from "recharts";
import { BaseWidget, WidgetIconButton } from "../common";
import { DateRangeSelect } from "../common/DateRangeSelect";
import { buildContractsQuery } from "../../hooks/buildContractsQuery";
import "../../styles/DisbursementsWidget.css";
import { formatNumber, getRangeDates } from "../../constants";
import {
  OverlayTrigger,
  Row,
  Col,
  Tooltip as BootstrapToolTip,
} from "react-bootstrap";
import { Download, BoxArrowUpRight, InfoCircle } from "react-bootstrap-icons";

export function DisbursementsWidget({
  data,
  range,
  onRangeChange,
  isLoading,
  error,
  onNavigate,
  onDownload,
}) {
  const { monthlyPoints, peakLabel } = useMemo(() => {
    if (!data?.points?.length) {
      return { monthlyPoints: [], peakLabel: "" };
    }

    const groups = {};
    data.points.forEach((p) => {
      const d = new Date(p.date);
      if (isNaN(d.getTime())) return;
      const key = `${d.getFullYear()}-${String(d.getMonth()).padStart(2, "0")}`;
      if (!groups[key]) {
        groups[key] = {
          sortKey: key,
          month: d.toLocaleDateString("en-US", { month: "short" }),
          count: 0,
        };
      }
      groups[key].count += p.count || 0;
    });

    const points = Object.values(groups).sort((a, b) =>
      a.sortKey.localeCompare(b.sortKey),
    );

    const peak = points.reduce(
      (best, p) => (p.count > (best?.count || 0) ? p : best),
      null,
    );

    return {
      monthlyPoints: points,
      peakLabel: peak ? `${peak.month} (${formatNumber(peak.count)})` : "",
    };
  }, [data]);

  const total = data?.total || 0;

  const resolvedDates = useMemo(() => getRangeDates(range), [range]);

  const handleNavigate = () => {
    onNavigate(
      "/contracts?" +
        buildContractsQuery({
          disbursementStartDate: resolvedDates.startDate,
          disbursementEndDate: resolvedDates.endDate,
        }),
    );
  };

  const titleNode = (
    <span className="cp-disb-widget__title-wrap">
      Disbursements
      <OverlayTrigger
        placement="right"
        popperConfig={{
          modifiers: [{ name: "offset", options: { offset: [0, 10] } }],
        }}
        overlay={
          <BootstrapToolTip id="cp-disb-info" className="cp-disb-tooltip">
            Reflects the number of disbursements, not contracts —
            <br />a single contract may have multiple disbursements.
          </BootstrapToolTip>
        }
      >
        <span
          className="cp-disb-widget__info-icon"
          aria-label="About disbursement counts"
          tabIndex={0}
        >
          <InfoCircle />
        </span>
      </OverlayTrigger>
    </span>
  );

  return (
    <BaseWidget
      title={titleNode}
      isLoading={isLoading}
      error={error}
      headerRight={
        <>
          <DateRangeSelect value={range} onChange={onRangeChange} />
          {onDownload && (
            <WidgetIconButton
              icon={<Download />}
              title="Download"
              onClick={onDownload}
            />
          )}
          <WidgetIconButton
            icon={<BoxArrowUpRight />}
            title="View all"
            onClick={handleNavigate}
          />
        </>
      }
    >
      {data && (
        <Row className="g-2 align-items-baseline cp-disb-widget__head">
          <Col xs="auto">
            <span className="cp-disb-widget__total">
              {total.toLocaleString()}
            </span>
            <span className="cp-disb-widget__total-label"> disbursed</span>
          </Col>
          {peakLabel && (
            <Col xs="auto">
              <span className="cp-disb-widget__peak">Peak: {peakLabel}</span>
            </Col>
          )}
        </Row>
      )}

      <div className="cp-disb-widget__chart">
        {monthlyPoints.length > 0 && (
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={monthlyPoints}
              margin={{ top: 5, right: 4, left: 4, bottom: 0 }}
            >
              <defs>
                <linearGradient id="cpBlueBar" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#185fa5" stopOpacity={0.95} />
                  <stop offset="100%" stopColor="#5ba0e5" stopOpacity={0.75} />
                </linearGradient>
              </defs>
              <CartesianGrid
                strokeDasharray="3 3"
                stroke="#eef0f3"
                vertical={false}
              />
              <XAxis
                dataKey="month"
                axisLine={false}
                tickLine={false}
                tick={{ fontSize: 10.5, fill: "#9ca3af" }}
              />
              <Tooltip
                cursor={{ fill: "rgba(37, 99, 235, 0.05)" }}
                contentStyle={{
                  backgroundColor: "#fff",
                  border: "1px solid #e5e7eb",
                  borderRadius: 8,
                  fontSize: 11,
                }}
                labelStyle={{ color: "#6b7280", fontWeight: 600 }}
                formatter={(value) => [value.toLocaleString(), "Disbursed"]}
              />
              <Bar
                dataKey="count"
                fill="url(#cpBlueBar)"
                radius={[4, 4, 0, 0]}
                maxBarSize={42}
                isAnimationActive={false}
              />
            </BarChart>
          </ResponsiveContainer>
        )}
      </div>
    </BaseWidget>
  );
}
